import React, { useEffect, useState, useRef } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, number as yupnumber, array as yuparray, date } from 'yup';
import moment from 'moment';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import Button from '@material-ui/core/Button';
import IsJuvenile from '../../../../Helpers/Dates/IsJuvenile';
import BeneficiaryForm from './BeneficiaryForm';
import { emptyStringToUndefined } from '../../../../Helpers/Formatters/EmptyStringToUndefined.js';
import Age from '../../../../Helpers/Dates/Age'
import { sub } from 'date-fns';
import { customValidators } from '../../../../Helpers/Validators/customValidators'
export default function Beneficiary(props) {
  var currentAge = Age(props.basicInfo.birthdate);
  const { register, unregister, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: Beneficiary.ValidationSchema(currentAge),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const isJuvenile = IsJuvenile(props.basicInfo.birthdate);

  useEffect(() => {
    props.formState.beneficiaries.forEach((bene, index) => {
      register({ name: `beneficiaries[${index}].state` });
      setValue(`beneficiaries[${index}].state`, bene.state);

      register({ name: `beneficiaries[${index}].beneficiaryType` });
      setValue(`beneficiaries[${index}].beneficiaryType`, bene.beneficiaryType);

      register({ name: `beneficiaries[${index}].relationship` });
      setValue(`beneficiaries[${index}].relationship`, bene.relationship);
    });
  }, [register]);

  const addNewBeneficiary = () => {
    const newBeneIndex = props.formState.beneficiaries.length;

    register({ name: `beneficiaries[${newBeneIndex}].state` });
    register({ name: `beneficiaries[${newBeneIndex}].beneficiaryType` });
    register({ name: `beneficiaries[${newBeneIndex}].relationship` });

    const newForm = Beneficiary.DefaultFormState().beneficiaries[0];
    props.onFieldChange('beneficiaries', [...props.formState.beneficiaries, newForm]);
  };

  const [showBenePercentageMessage, setShowBenePercentageMessage] = useState(false)
  const beneErrorMessage = useRef()

  const submitForm = () => {

    let benePercentTotal = 0;
    let contingentPercentTotal = 0;
    let contingentBenesPresent = false;
    const radix = 10;
    props.formState.beneficiaries.forEach((bene, index) => {

      if (bene.beneficiaryType === 'Contingent') {
        contingentPercentTotal += parseInt(bene.percentage, radix)
        contingentBenesPresent = true
      }
      else {
        benePercentTotal += parseInt(bene.percentage, radix)
      }
    })

    if (benePercentTotal !== 100 || (contingentBenesPresent && contingentPercentTotal !== 100)) {
      setShowBenePercentageMessage(true)
      window.scrollTo(0, beneErrorMessage.current.offsetTop)
    }
    else {
      props.onNext()
    }
  }

  const forms = props.formState.beneficiaries.map((beneficiaryFormState, index) => (
    <BeneficiaryForm
      formState={beneficiaryFormState}
      isJuvenile={isJuvenile}
      onRemove={() => {
        // unregister registered inputs for removed bene
        unregister(`beneficiaries[${index}].firstName`);
        unregister(`beneficiaries[${index}].lastName`);
        unregister(`beneficiaries[${index}].percentage`);
        unregister(`beneficiaries[${index}].ssn`);
        unregister(`beneficiaries[${index}].address`);
        unregister(`beneficiaries[${index}].city`);
        unregister(`beneficiaries[${index}].zip`);
        unregister(`beneficiaries[${index}].phone`);
        unregister(`beneficiaries[${index}].email`);
        unregister(`beneficiaries[${index}].birthdate`);
        unregister(`beneficiaries[${index}].beneficiaryType`);
        unregister(`beneficiaries[${index}].relationship`);
        unregister(`beneficiaries[${index}].state`);

        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.filter((form, formIndex) => formIndex !== index)
        );
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      errors={errors.beneficiaries && errors.beneficiaries[index]}
      isRemove={ index != 0 && index == props.formState.beneficiaries.length - 1}
      unavailableStates={props.unavailableStates}
    />
  ));

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={5}>
          <div className="formPageTitle">Beneficiary Information</div>

          {
            showBenePercentageMessage &&
            <div ref={beneErrorMessage} className="text-danger text-center">Primary and contingent beneficiary percentages must add up to 100</div>
          }

          <form className="quote-form" onSubmit={handleSubmit(submitForm)}>
            {forms}

            <Button color="primary" onClick={addNewBeneficiary} className="d-flex m-auto">
              Add a beneficiary
            </Button>

            <Row>
              <Col>
                <Form.Group>
                  <Button variant="outlined" onClick={props.onBack} fullWidth>
                    Back
                  </Button>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    Continue
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </form>
        </Col>
      </Row>
    </Container>
  );
}

Beneficiary.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

Beneficiary.ValidationSchema = (currentAge) => {
  return object().shape({
    beneficiaries: yuparray(
      object({
        firstName: customValidators.string()
          .isValidNameLimitedSpecialChar('First Name is required.', true, false),
        middleInitial: string()
          .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
          .notRequired(),
        lastName: customValidators.string()
          .isValidNameLimitedSpecialChar('Last Name is required.', true, false),
        percentage: yupnumber()
          .typeError('Must be a number')
          .transform(emptyStringToUndefined)
          .min(1)
          .max(100, 'Percentage must be between 1 and 100.')
          .required('Percentage is required.'),
        ssn: string()
          .matches(/(^(?:\d{3}-?\d{2}-?\d{4})?$)/, 'Social Security Number must be formatted as "123-45-6789".')
          .notRequired(),
        beneficiaryType: string().required('Beneficiary Class is required.'),
        address: customValidators.string()
          .isValidNameLimitedSpecialChar('Address is required.', true, true),
        address2: customValidators.string().isValidNameLimitedSpecialChar('', false, true),
        city: customValidators.string()
          .isValidNameLimitedSpecialChar('City is required.', true, false),
        state: string().required('State is required.'),
        zip: string().required('Zip code is required.').matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
        phone: customValidators.string()
          .isValidPhone('Phone Number must be formatted as "(123) 456-7890".')
          .required('Phone is required.'),
        email: string().required('Email address is required.').email('Email address must be valid.'),
        birthdate: date().when('relationship', {
          is: '13',
          then: date()
            .transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .required('Date of Birth is required.')
            .min(sub(new Date(), { years: currentAge + 1 }), `Child's age cannot be greater than parent.`)
            .max(moment(new Date()).subtract(1, 'days'), 'Birthdate must be in past.'),
          otherwise: date().transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .required('Date of Birth is required.')
            .max(moment(new Date()).subtract(1, 'days'), 'Birthdate must be in past.'),
        }),
        relationship: string().required('Relationship is required.'),
      })
    ),
  });
};
Beneficiary.DefaultFormState = (prefill = false) => {
  return {
    agreeToTerms: prefill ? true : false,
    beneficiaries: [
      {
        firstName: prefill ? 'Rebecca' : '',
        middleInitial: prefill ? 'S' : '',
        lastName: prefill ? 'Puga' : '',
        percentage: prefill ? 100 : '',
        ssn: prefill ? '123-45-6789' : '',
        beneficiaryType: prefill ? 'Primary' : '',
        address: prefill ? '123 Main Street' : '',
        address2: prefill ? '#123' : '',
        city: prefill ? 'Boston' : '',
        state: prefill ? 'ID' : '',
        zip: prefill ? '02110' : '',
        phone: prefill ? '(123) 456-7890' : '',
        email: prefill ? 'user.name@example.com' : '',
        birthdate: prefill ? '09/19/1993' : '',
        relationship: prefill ? '9' : '',
      },
    ],
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Securian/WL/steps/Beneficiary.js